import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  Title,
  TextInput,
  Textarea,
  FileInput,
  List,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconUpload, IconPlus } from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import { useCallback, useState } from "react";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

const APP_RELEASES_QUERY = gql`
  query appReleases($marketplaceAppId: ID!) {
    appReleases(marketplaceAppId: $marketplaceAppId) {
      id
      version
      status
      releaseNotes
      reviewResult {
        passed
        checks {
          name
          passed
          details
        }
      }
      reviewedAt
      createdAt
    }
  }
`;

const CREATE_RELEASE_MUTATION = gql`
  mutation appCreateRelease(
    $marketplaceAppId: ID!
    $input: AppCreateReleaseInput!
  ) {
    appCreateRelease(marketplaceAppId: $marketplaceAppId, input: $input) {
      release {
        id
        version
        status
      }
      extensionUploadUrl {
        url
        fields {
          key
          value
        }
      }
      moduleUploadUrl {
        url
        fields {
          key
          value
        }
      }
    }
  }
`;

const FINALIZE_RELEASE_MUTATION = gql`
  mutation appFinalizeRelease($releaseId: ID!) {
    appFinalizeRelease(releaseId: $releaseId) {
      valid
      errors
    }
  }
`;

const PUBLISH_RELEASE_MUTATION = gql`
  mutation appPublishRelease($releaseId: ID!) {
    appPublishRelease(releaseId: $releaseId) {
      id
      version
      status
    }
  }
`;

interface PresignedUrlData {
  url: string;
  fields: Array<{ key: string; value: string }>;
}

interface ReviewCheck {
  name: string;
  passed: boolean;
  details: string | null;
}

interface ReviewResult {
  passed: boolean;
  checks: ReviewCheck[];
}

interface Release {
  id: string;
  version: string;
  status: string;
  releaseNotes: string | null;
  reviewResult: ReviewResult | null;
  reviewedAt: string | null;
  createdAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  PENDING_REVIEW: "yellow",
  APPROVED: "green",
  REJECTED: "red",
};

interface CreateReleaseFormProps {
  appId: string;
  createRelease: (options: {
    variables: Record<string, unknown>;
  }) => Promise<{ data?: any }>;
  finalizeRelease: (options: {
    variables: Record<string, unknown>;
  }) => Promise<{ data?: any }>;
  onSuccess: () => void;
}

function CreateReleaseForm({
  appId,
  createRelease,
  finalizeRelease,
  onSuccess,
}: CreateReleaseFormProps) {
  const [version, setVersion] = useState("");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [extensionFile, setExtensionFile] = useState<File | null>(null);
  const [moduleFile, setModuleFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleCreateRelease = async () => {
    if (!version) {
      notifications.show({
        title: "Error",
        message: "Version is required",
        color: "red",
      });
      return;
    }

    setUploading(true);
    try {
      const { data: createData } = await createRelease({
        variables: {
          marketplaceAppId: appId,
          input: {
            version,
            manifest: {
              version: "1.0",
              permissions: ["organization:marketplace:view"],
              extensions: [],
              webhookEvents: [],
              metafieldNamespaces: [],
              frontendModules: [],
            },
            releaseNotes: releaseNotes || undefined,
            hasExtensionBundle: !!extensionFile,
            hasModuleBundle: !!moduleFile,
            extensionBundleSize: extensionFile?.size,
            moduleBundleSize: moduleFile?.size,
          },
        },
      });

      const result = createData?.appCreateRelease;
      if (!result) {
        throw new Error("Failed to create release");
      }

      if (extensionFile && result.extensionUploadUrl) {
        await uploadToPresigned(result.extensionUploadUrl, extensionFile);
      }
      if (moduleFile && result.moduleUploadUrl) {
        await uploadToPresigned(result.moduleUploadUrl, moduleFile);
      }

      const { data: finalizeData } = await finalizeRelease({
        variables: { releaseId: result.release.id },
      });

      const validation = finalizeData?.appFinalizeRelease;
      if (validation && !validation.valid) {
        const errorMessages: string[] = Array.isArray(validation.errors)
          ? validation.errors
          : [];
        notifications.show({
          title: "Bundle Validation Failed",
          message:
            errorMessages.length > 0
              ? errorMessages.join("; ")
              : "Bundle failed validation.",
          color: "red",
          autoClose: 10000,
        });
      } else {
        notifications.show({
          title: "Release Created",
          message: `Version ${version} submitted for review.`,
          color: "green",
          autoClose: 5000,
        });
      }

      onSuccess();
    } catch (error) {
      notifications.show({
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to create release",
        color: "red",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack gap="md">
      <TextInput
        label="Version"
        placeholder="1.0.0"
        value={version}
        onChange={(e) => setVersion(e.currentTarget.value)}
        required
      />
      <Textarea
        label="Release Notes"
        placeholder="What changed in this release?"
        value={releaseNotes}
        onChange={(e) => setReleaseNotes(e.currentTarget.value)}
      />
      <FileInput
        label="Extension Bundle (.js)"
        placeholder="Upload extension bundle"
        accept=".js"
        value={extensionFile}
        onChange={(file) => {
          if (file && file.size > MAX_FILE_SIZE_BYTES) {
            notifications.show({
              title: "File Too Large",
              message: `Extension bundle must be under 5MB. Selected file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`,
              color: "red",
            });
            return;
          }
          setExtensionFile(file);
        }}
        leftSection={<IconUpload size={14} />}
      />
      <FileInput
        label="Module Bundle (.js)"
        placeholder="Upload module bundle"
        accept=".js"
        value={moduleFile}
        onChange={(file) => {
          if (file && file.size > MAX_FILE_SIZE_BYTES) {
            notifications.show({
              title: "File Too Large",
              message: `Module bundle must be under 5MB. Selected file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`,
              color: "red",
            });
            return;
          }
          setModuleFile(file);
        }}
        leftSection={<IconUpload size={14} />}
      />
      <Button onClick={handleCreateRelease} loading={uploading} fullWidth>
        Create & Submit for Review
      </Button>
    </Stack>
  );
}

export function AppReleases() {
  const { appId } = useParams<{ appId: string }>();
  const { data, loading, refetch } = useQuery<any>(APP_RELEASES_QUERY, {
    variables: { marketplaceAppId: appId },
    skip: !appId,
  });
  const [createRelease] = useMutation<any>(CREATE_RELEASE_MUTATION);
  const [finalizeRelease] = useMutation<any>(FINALIZE_RELEASE_MUTATION);
  const [publishRelease] = useMutation<any>(PUBLISH_RELEASE_MUTATION);

  const openCreateReleaseModal = useCallback(() => {
    if (!appId) return;
    const modalId = `create-release-${appId}`;
    modals.open({
      modalId,
      title: "Create New Release",
      size: "lg",
      children: (
        <CreateReleaseForm
          appId={appId}
          createRelease={createRelease}
          finalizeRelease={finalizeRelease}
          onSuccess={() => {
            modals.close(modalId);
            refetch();
          }}
        />
      ),
    });
  }, [appId, createRelease, finalizeRelease, refetch]);

  const handlePublish = useCallback(
    async (releaseId: string) => {
      try {
        await publishRelease({ variables: { releaseId } });
        notifications.show({
          title: "Published",
          message: "Release has been published.",
          color: "green",
        });
        refetch();
      } catch (error) {
        notifications.show({
          title: "Publish Failed",
          message:
            error instanceof Error
              ? error.message
              : "Failed to publish release",
          color: "red",
        });
      }
    },
    [publishRelease, refetch],
  );

  const releases: Release[] = data?.appReleases ?? [];

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3}>Releases</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={openCreateReleaseModal}
        >
          Create Release
        </Button>
      </Group>

      {loading && <Text c="dimmed">Loading releases...</Text>}

      {releases.length === 0 && !loading && (
        <Card withBorder padding="xl">
          <Text c="dimmed" ta="center" py="xl">
            No releases yet. Create your first release to submit for review.
          </Text>
        </Card>
      )}

      {releases.map((release) => (
        <Card key={release.id} withBorder>
          <Group justify="space-between">
            <Group gap="md">
              <Text fw={600}>v{release.version}</Text>
              <Badge
                color={STATUS_COLORS[release.status] ?? "gray"}
                variant="light"
              >
                {release.status.replace("_", " ")}
              </Badge>
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                {new Date(release.createdAt).toLocaleDateString()}
              </Text>
              {release.status === "PENDING_REVIEW" && (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => handlePublish(release.id)}
                >
                  Force Publish
                </Button>
              )}
            </Group>
          </Group>
          {release.releaseNotes && (
            <Text size="sm" c="dimmed" mt="xs">
              {release.releaseNotes}
            </Text>
          )}
          {release.status === "REJECTED" && release.reviewResult && (
            <Stack gap={4} mt="xs">
              <Text size="sm" c="red" fw={500}>
                Review failed:
              </Text>
              <List size="sm" spacing={2}>
                {release.reviewResult.checks
                  .filter((check) => !check.passed)
                  .map((check) => (
                    <List.Item key={check.name}>
                      <Text size="sm" c="red">
                        {check.name}
                        {check.details ? `: ${check.details}` : ""}
                      </Text>
                    </List.Item>
                  ))}
              </List>
            </Stack>
          )}
        </Card>
      ))}

    </Stack>
  );
}

/**
 * Upload a file to an S3 presigned POST URL.
 */
async function uploadToPresigned(presigned: PresignedUrlData, file: File) {
  const formData = new FormData();
  for (const field of presigned.fields) {
    formData.append(field.key, field.value);
  }
  formData.append("file", file);

  const response = await fetch(presigned.url, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }
}
